import React, {useEffect, useState} from 'react';
import {Autosuggest, Select} from '@amzn/awsui-components-react/polaris';
import {DataKeyInput} from 'src/models/common/DataSourceRecord';
import CONSTANTS from 'src/utils/constants';
import ServiceCollection from 'src/services/ServiceCollection';
import TPEAction from 'src/models/common/TPEAction';
import 'src/assets/styles/MagicBox.scss';
import Placeholder from 'src/models/tp-allocation/Placeholder';
import {Box, Button, Link, StatusIndicator} from "@amzn/awsui-components-react";
import {MultiAllocationSheetData} from "src/models/multidimensional-allocation/MultiAllocationSheetData";
import CustomCOASelectorModal from 'src/components/calculation-builder/data-sources/CustomCOASelectorModal';
import {MULTI_ALLOCATION_DATA_SOURCE_ACTIONS} from 'src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesReducer';
import {MDAResultsData, NO_RESULT_FILE} from 'src/components/multidimensional-allocation/hooks/useMDAResultsData';
import {computeExecutionTotal} from 'src/components/multidimensional-allocation/hooks/executionTotal';
import {isCommonBalancePoolMdaEnabled, isDasInMdaEnabled} from 'src/services/FeatureFlagsService';
import {
    kindOf, kindToSave, selectedSheetOption, sheetDetailsUrl, sheetOptionValue, sheetsForSegment, toSheetOption,
} from 'src/services/multidimensional-allocation/data-sources/ratioSheetOptions';

interface MagicBoxActions {
    SET_FX_INPUTS: TPEAction,
    SET_DATA_KEY_INPUT: TPEAction,
    SET_DATA_KEY_COA_INPUT: TPEAction,
    TOGGLE_CATEGORY_EXPANSION: TPEAction,
    UPDATE_RECORD_FIELD: TPEAction,
}

/**
 * COA input interface for multidimensional allocation with single-value autosuggestion.
 * Displays counters (0 or 1) and provides Company/GL Account inputs with selected COA segments.
 */
export default function MultidimensionalAllocationDataSourcesMagicBox(props: {
    recordBeingEdited: any | undefined,
    services: ServiceCollection,
    dispatch: React.Dispatch<TPEAction>,
    coaTypes: string[],
    sevenSegmentLOV: Map<string, string[]>,
    selectedCustomCoaValues?: Map<string, string>,
    setPullDataSourceBalance?: (datasourceId: string) => void,
    ACTIONS: MagicBoxActions,
    filteredAllocationSheets: MultiAllocationSheetData[],
    /** Every sheet of the worksheet; a pool row picks from these by its own segment. */
    allocationSheets?: MultiAllocationSheetData[],
    /** Names of every sheet by picker value, to name a saved sheet the picker does not offer. */
    sheetNames?: Map<string, string>,
    allocationSheetsLoading: boolean,
    pullBalanceLoading?: boolean,
    isViewingHistoricalData?: boolean,
    /** Historical mode: the execution status is being re-fetched for this record, so its result link is not yet usable. */
    resultLinkPending?: boolean,
    /** The record's result file, loaded once by the container and shared with the results table. */
    resultFile?: MDAResultsData
}) {
    const {
        recordBeingEdited,
        services,
        dispatch,
        coaTypes,
        selectedCustomCoaValues,
        sevenSegmentLOV,
        setPullDataSourceBalance,
        ACTIONS,
        filteredAllocationSheets,
        allocationSheets = [],
        sheetNames,
        allocationSheetsLoading,
        pullBalanceLoading = false,
        isViewingHistoricalData = false,
        resultLinkPending = false,
        resultFile = NO_RESULT_FILE
    } = props;
    const {
        dataKeyInput = {selectedCompanies: '', selectedAccounts: '', selectedCOA: new Map<string, string>()},
        errors = new Map<string, string>(),
        isEditing = false,
    } = recordBeingEdited || {};
    const {selectedCompanies, selectedAccounts, selectedCOA} = dataKeyInput;

    // The past view totals the execution's own result file; the current view shows the definition row's total.
    const totalBalancePull = isViewingHistoricalData
        ? renderExecutionTotal(resultLinkPending, resultFile, recordBeingEdited?.s3PresignUrl)
        : (recordBeingEdited?.totalBalance || '-');

    // LG Code (segment 8) is only valid for Secondary Ledger datasources; hide it for others
    // so it cannot be selected on (or saved to) a non-SL record.
    const visibleCoaTypes = recordBeingEdited?.datasource === CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER
        ? coaTypes
        : coaTypes.filter((coaType) => coaType !== CONSTANTS.COA_SEGMENT_MAPPING.LG_CODE.UI);

    // A pool-backed data source takes its balance from the pool, so the Company / GL Account and COA
    // segment inputs are hidden. "Pull balance" stays: the per-row run reads the pool for a pool row.
    const isPoolRow = isCommonBalancePoolMdaEnabled()
        && recordBeingEdited?.datasource === CONSTANTS.DATA_SOURCE_TYPES.COMMON_BALANCE_POOL;

    // Every row picks from the full sheet list by its own segment (sheetsForSegment, below), never from the shared
    // filtered list: a segment pick on one row narrows that list for the whole page, and Cancel does not undo it, so
    // a row on another segment would get an empty picker.
    const sheetsForRow = allocationSheets;

    const getSingleCount = (value: string) => {
        if (value == null || value.trim() == '') {
            return 0;
        }
        return 1;
    };

    const buildDataKeyCounter = () => {
        const counterArray: any[] = [];
        visibleCoaTypes.filter((coaType) => selectedCOA.has(coaType)).map((coaType) => counterArray.push({
            key: coaType,
            count: getSingleCount(selectedCOA.get(coaType) || '')
        }));
        return counterArray;
    }

    const pullBalance = () => {
        dispatch(ACTIONS.SET_FX_INPUTS);
        if (setPullDataSourceBalance) {
            setPullDataSourceBalance(new Date().toISOString())
        }
    }

    const getSelectedSheetUrl = () =>
        sheetDetailsUrl(recordBeingEdited?.allocationSheetId, recordBeingEdited?.allocationSheetVersion,
            recordBeingEdited?.ratioSheetKind);

    return (
        <div className="dataKeyTable">
            {!isPoolRow &&
                <div>
                    <span className="dataKeyCounterSpacer">Company: {getSingleCount(selectedCompanies)}</span>
                    <span className="dataKeyCounterSpacer">GL Accounts: {getSingleCount(selectedAccounts)}</span>
                    {buildDataKeyCounter().map((dataKey) => <span key={dataKey.key}
                                                                  className="dataKeyCounterSpacer">{dataKey.key}: {dataKey.count}</span>)}
                </div>
            }
            <div className="dataKeyTableMainSelection">
                <div className="dataKeyTableContainerSelection">
                    <DataKeyInputLeftColumn key="dkil" coaTypes={visibleCoaTypes} selectedCOA={selectedCOA} errors={errors} isViewingHistoricalData={isViewingHistoricalData} isPoolRow={isPoolRow}/>
                    <DataKeyInputRightColumn key="dkir"
                                             isEditing={isEditing}
                                             dataKeyInput={dataKeyInput}
                                             services={services}
                                             dispatch={dispatch}
                                             ACTIONS={ACTIONS}
                                             coaTypes={visibleCoaTypes}
                                             sevenSegmentLOV={sevenSegmentLOV}
                                             selectedCustomCoaValues={selectedCustomCoaValues}
                                             recordBeingEdited={recordBeingEdited}
                                             filteredAllocationSheets={sheetsForRow}
                                             sheetNames={sheetNames}
                                             allocationSheetsLoading={allocationSheetsLoading}
                                             isViewingHistoricalData={isViewingHistoricalData}
                                             totalBalancePull={totalBalancePull}
                                             isPoolRow={isPoolRow}
                                             getSelectedSheetUrl={getSelectedSheetUrl}
                    />
                </div>
                {!isViewingHistoricalData &&
                    <div className="dataKeyTableMainSelectionButton" style={{ gridColumn: 'span 2' }}>
                        {<Box textAlign="center"><Button
                            variant="normal"
                            disabled={isEditing || pullBalanceLoading}
                            disabledReason={pullBalanceLoading ? "Pulling balance..." : "Please save the data source before pulling balance"}
                            loading={pullBalanceLoading}
                            onClick={pullBalance}>
                            {pullBalanceLoading ? 'Pulling balance...' : 'Pull balance'}
                        </Button></Box>}
                    </div>
                }
            </div>
        </div>
    )
}

/**
 * Displays error messages for COA segments.
 * @param props
 */
function COAErrorDisplay(props: { coaSegment: string, errors: Map<string, string> }) {
    const {coaSegment, errors} = props;

    if (errors.has(coaSegment)) {
        const coaErrors = errors.get(coaSegment) || '';
        if (coaErrors.includes('Invalid value(s): ')) {
            return <span className="dataSourceInlineError">*Invalid value(s)</span>;
        } else {
            return <span className="dataSourceInlineError">*{coaErrors}</span>;
        }
    }
    return <span>&nbsp;</span>;
}

/**
 * Renders left column with COA segment labels and error displays.
 * @param props
 */
function DataKeyInputLeftColumn(props: {
    coaTypes: string[],
    selectedCOA: Map<string, string>,
    errors: Map<string, string>,
    isViewingHistoricalData?: boolean,
    isPoolRow?: boolean
}) {
    const {coaTypes, selectedCOA, errors, isViewingHistoricalData, isPoolRow = false} = props;

    // The labels and the inputs are two separate columns of one grid, so a row hidden here must be hidden
    // in DataKeyInputRightColumn on the same condition or every row below it reads against the wrong label.
    return (
        <div className="magicGridStickLeft">
            {!isPoolRow && <div className="magicGridLeft"><span>Company*</span><COAErrorDisplay
                coaSegment={CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI} errors={errors}/></div>}
            {!isPoolRow && <div className="magicGridLeft"><span>GL Account*</span><COAErrorDisplay
                coaSegment={CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI} errors={errors}/></div>}
            {!isPoolRow && selectedCOA != undefined && coaTypes.filter((coaType) => selectedCOA.has(coaType)).map((coaType) =>
                <div key={coaType} className="magicGridLeft"><span>{coaType}</span><COAErrorDisplay coaSegment={coaType}
                                                                                                    errors={errors}/>
                </div>)
            }
            <div className="magicGridLeftDropDown"><span>Allocation Sheet*</span><COAErrorDisplay
                coaSegment="allocationSheetId" errors={errors}/></div>
            {isViewingHistoricalData && (
                <div className="magicGridLeft"><span>Allocation Sheet Link</span></div>
            )}
            <div className="magicGridLeft"><span>Total Balance Pull</span></div>
        </div>
    )
}

/**
 * Single-value autosuggest component for COA input.
 * @param props
 */
function COAAutosuggest(props: {
    className: string,
    lov: string[] | undefined,
    coaType: string,
    initialValue: string,
    isEditing: boolean,
    selectedCustomCoaValues?: Map<string, string>,
    placeholders?: Placeholder[],
    onBlur: (coaType: string, value: string) => any,
    handleCustomRollup: () => any
}) {
    const {
        className,
        lov,
        coaType,
        initialValue,
        onBlur,
        isEditing,
        selectedCustomCoaValues,
        placeholders = [],
        handleCustomRollup
    } = props;
    const [options, setOptions] = React.useState([] as any[]);
    const [value, setValue] = React.useState(initialValue);

    const customRollupValue = 'Custom rollup';
    const customRollupOption = { value: customRollupValue };

    useEffect(() => {
        if (selectedCustomCoaValues?.has(coaType)) {
            const addedComma = value && value.trim() && !value.trim().endsWith(',') ? ', ' : '';
            const newValue = (value || '') + addedComma + (selectedCustomCoaValues.get(coaType) || '');
            handleChange(newValue);
        }
    }, [selectedCustomCoaValues])

    const handleLoadItems = (event: any) => {
        let {filteringText} = event.detail;

        if (filteringText != null && filteringText.includes(',')) {
            filteringText = filteringText.substring(filteringText.lastIndexOf(',')).replace(',', '').replace(' ', '');
        }

        const placeholderOptions = placeholders.map(x => {
            return {value: `<${x.placeholderName}>`}
        });

        if (filteringText == null || filteringText === '') {
            setOptions([...placeholderOptions, customRollupOption]);
            return;
        }

        const filteredOptions = lov?.filter(x => x.toLowerCase().startsWith(filteringText.toLowerCase())).map((val) => {
            return {value: val}
        });
        setOptions(filteredOptions ? [...placeholderOptions, customRollupOption, ...filteredOptions] : [customRollupOption]);
    }

    const beautifyCOAValue = () => {
        if (value == null) {
            return '';
        }
        const valueArr = value.split(',');
        // Trim and remove duplicates
        const beautifiedArr = valueArr.filter(x => x.trim() !== '').map(x => x.trim()).filter((value, index, self) => index === self.indexOf(value));
        const beautifiedString = beautifiedArr.join(', ');
        setValue(beautifiedString);
        return beautifiedString;
    }

    const handleChange = (selectedValue: any) => {
        if (selectedValue === customRollupValue) {
            return;
        }
        setValue(selectedValue);
    }

    const handleSelect = (selectedValue: string) => {
        if (selectedValue === customRollupValue) {
            handleCustomRollup();
            return;
        }

        if (value.includes(',')) {
            const newValue = value.substring(0, value.lastIndexOf(',')) + ', ' +  selectedValue;
            setValue(newValue);
        } else {
            setValue(selectedValue);
        }
    }

    return (
        <Autosuggest
            data-class={`coaAutosuggest ${className}`}
            onChange={({detail}) => handleChange(detail.value)}
            onBlur={() => {
                onBlur(coaType, beautifyCOAValue());
            }}
            onSelect={({detail}) => handleSelect(detail.value)}
            value={value}
            options={options}
            enteredTextLabel={val => `Use: "${val}"`}
            placeholder="Select or enter a value"
            empty="No matches found"
            loadingText="Loading"
            onLoadItems={handleLoadItems}
            filteringType="manual"
            statusType={lov == undefined ? 'loading' : 'finished'}
            disabled={!isEditing}
        />
    );
}

// A loading or failed result file must not read as "no total"; the results table below shows the same file's full error.
function renderExecutionTotal(linkPending: boolean, resultFile: MDAResultsData, resultUrl?: string): React.ReactNode {
    if (linkPending || resultFile.loading) {
        return <StatusIndicator type="loading">Loading total</StatusIndicator>;
    }
    if (resultFile.error) {
        return <StatusIndicator type="error">Result file could not be loaded</StatusIndicator>;
    }
    const total = resultUrl ? computeExecutionTotal(resultFile.excelData, resultFile.headers) : undefined;
    return total?.toFixed(2) ?? '-';
}

/**
 * Renders right column with COA input fields and allocation sheet selector.
 * @param props
 */
function DataKeyInputRightColumn(props: {
                                     isEditing: boolean,
                                     dataKeyInput: DataKeyInput,
                                     services: ServiceCollection,
                                     dispatch: React.Dispatch<TPEAction>,
                                     coaTypes: string[],
                                     sevenSegmentLOV: Map<string, string[]>,
                                     selectedCustomCoaValues?: Map<string, string>,
                                     placeholdersMap?: Map<string, Placeholder[]>,
                                     ACTIONS: MagicBoxActions,
                                     recordBeingEdited: any,
                                     filteredAllocationSheets: MultiAllocationSheetData[],
                                     sheetNames?: Map<string, string>,
                                     allocationSheetsLoading: boolean,
                                     isViewingHistoricalData?: boolean,
                                     totalBalancePull: React.ReactNode,
                                     isPoolRow?: boolean,
                                     getSelectedSheetUrl: () => string | null
                                 }
) {
    const {
        isEditing,
        services,
        dispatch,
        coaTypes,
        sevenSegmentLOV,
        dataKeyInput,
        ACTIONS,
        selectedCustomCoaValues,
        placeholdersMap,
        recordBeingEdited,
        filteredAllocationSheets,
        sheetNames,
        allocationSheetsLoading,
        isViewingHistoricalData,
        totalBalancePull,
        isPoolRow = false,
        getSelectedSheetUrl
    } = props;
    const {selectedCompanies, selectedAccounts, selectedCOA} = dataKeyInput;

    // This is needed for refreshing the auto suggest components for viewing the data key for different records in succession
    // The disabled auto suggest does not refresh with the updated values without changing the key
    const [keyCount, setKeyCount] = useState(0);
    const [showCustomCoaModal, setShowCustomCoaModal] = useState(false);
    const [customCoaSegment, setCustomCoaSegment] = useState('');
    const [customCoaType, setCustomCoaType] = useState('');

    useEffect(() => {
        setKeyCount(keyCount + 1);
    }, [isEditing])

    const setSelectedDataKey = (key: string, value: string) => {
        dispatch(ACTIONS.SET_DATA_KEY_INPUT.withPayload({key: key, value: value}));
    };

    const setSelectedCoa = (key: string, value: string) => {
        dispatch(ACTIONS.SET_DATA_KEY_COA_INPUT.withPayload({key: key, value: value}));
    };

    const handleCustomRollup = (coaSegment: string, coaType: string) => {
        setCustomCoaSegment(coaSegment);
        setCustomCoaType(coaType);
        setShowCustomCoaModal(true);
    }

    const handleCustomRollupClose = () => {
        setShowCustomCoaModal(false);
        setCustomCoaSegment('');
        setCustomCoaType('');
    }

    const handleSheetSelection = ({detail}: any) => {
        const selectedValue = detail.selectedOption?.value;
        if (selectedValue && filteredAllocationSheets) {
            const sheet = filteredAllocationSheets.find((s: MultiAllocationSheetData) =>
                sheetOptionValue(s.kind, s.sheetId) === selectedValue);
            if (sheet) {
                const kind = kindOf(sheet.kind);
                // One new action per field. The shared ACTIONS.UPDATE_RECORD_FIELD is a single object whose
                // withPayload overwrites its payload, so dispatching it three times hands React one object, and
                // the updates React applies at render all see the last payload.
                const updateField = (fieldName: string, value: unknown) => dispatch(
                    new TPEAction(ACTIONS.UPDATE_RECORD_FIELD.type).withPayload({fieldName, value}));
                updateField('allocationSheetId', sheet.sheetId);
                // A derived sheet is always read at its live version.
                updateField('allocationSheetVersion', kind === 'DERIVED' ? 0 : sheet.sheetVersion);
                updateField('ratioSheetKind', kindToSave(kind, isDasInMdaEnabled()));
            }
        }
    };

    // Uploaded and derived sheets in one list, each tagged with its kind, on the data source's own segment.
    const sheetOptions = sheetsForSegment(filteredAllocationSheets, recordBeingEdited?.allocationSegment)
        .map(toSheetOption);

    // A saved sheet missing from the list is still shown, marked unavailable once the lists have loaded.
    const currentSelection = selectedSheetOption(sheetOptions, recordBeingEdited?.allocationSheetId,
        recordBeingEdited?.ratioSheetKind, sheetNames, allocationSheetsLoading);

    return (
        <div className="magicGridRightContainer">
            {/* Paired with DataKeyInputLeftColumn's Company / GL Account labels: hide both or neither. */}
            {!isPoolRow && <div className="magicGridRightInput">
                <COAAutosuggest
                    key={`companyAutoSuggest${keyCount}`}
                    className="companiesInput"
                    coaType="selectedCompanies"
                    initialValue={selectedCompanies}
                    onBlur={setSelectedDataKey}
                    lov={sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI)}
                    isEditing={isEditing}
                    selectedCustomCoaValues={selectedCustomCoaValues}
                    placeholders={placeholdersMap != null ? placeholdersMap.get(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI) : undefined}
                    handleCustomRollup={() => handleCustomRollup(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI, 'selectedCompanies')}
                />
            </div>}
            {!isPoolRow && <div className="magicGridRightInput">
                <COAAutosuggest
                    key={`accountAutoSuggest${keyCount}`}
                    className="accountsInput"
                    coaType="selectedAccounts"
                    initialValue={selectedAccounts}
                    onBlur={setSelectedDataKey}
                    lov={sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI)}
                    isEditing={isEditing}
                    selectedCustomCoaValues={selectedCustomCoaValues}
                    placeholders={placeholdersMap != null ? placeholdersMap.get(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI) : undefined}
                    handleCustomRollup={() => handleCustomRollup(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI, 'selectedAccounts')}
                />
            </div>}
            {!isPoolRow && selectedCOA != undefined && coaTypes.filter((coaType) => selectedCOA.has(coaType)).map((coaType) =>
                <div className="magicGridRightInput">
                    <COAAutosuggest
                        key={`${coaType.replace(' ', '_')}AutoSuggest${keyCount}`}
                        className={`${coaType.replace(' ', '_')}Input`}
                        coaType={coaType}
                        initialValue={selectedCOA.get(coaType) || ''}
                        onBlur={setSelectedCoa}
                        lov={sevenSegmentLOV.get(coaType)}
                        isEditing={isEditing}
                        selectedCustomCoaValues={selectedCustomCoaValues}
                        handleCustomRollup={() => handleCustomRollup(coaType, coaType)}
                    />
                </div>
            )}
            <div className="magicGridRightInput">
                <Select
                    key={`allocationSheetSelect${keyCount}`}
                    className="allocationSheetInput"
                    placeholder="Select allocation sheet"
                    selectedOption={currentSelection}
                    onChange={handleSheetSelection}
                    options={sheetOptions}
                    empty={"No allocation sheet available for selected segment"}
                    disabled={!isEditing}
                />
            </div>

            {isViewingHistoricalData && (() => {
                const sheetUrl = getSelectedSheetUrl();
                return sheetUrl ? (
                    <div className="magicBoxReadOnlyField">
                        <Link
                            variant="primary"
                            href={sheetUrl}
                            external
                            externalIconAriaLabel="Opens in a new tab"
                        >
                            link
                        </Link>
                    </div>
                ) : '-';
            })()}

            <div className="magicBoxReadOnlyField">
                {totalBalancePull}
            </div>
            <CustomCOASelectorModal 
                visible={showCustomCoaModal}
                coaSegment={customCoaSegment}
                coaType={customCoaType}
                services={services}
                onSelectedCOAApplied={(payload:any)=> {dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_SELECTED_CUSTOM_COA.withPayload(payload))}}
                onClose={handleCustomRollupClose} />
        </div>
    )
}
